import { useIntl } from 'react-intl';
import type { BillLimitDto } from '@ordr-as/ordr-service-types/epos';
import { useToast } from '@ordr-as/umami-react/toast';
import { useLocation } from '../../../../../../context/LocationContext';
import { useUpdateBillLimit } from '../../../../../../hooks/useDigitalBillApi';

/**
 * Cancels the bill's pending limit change. There is no cancel endpoint: re-applying the
 * current limit as a direct change supersedes the pending request. When the very first
 * set is still pending the bill has no applied limit yet, so cancelling removes it
 * (maxAmount null).
 */
export const useCancelBillLimitRequest = (reference: string, billLimit: BillLimitDto | undefined) => {
  const intl = useIntl();
  const toasts = useToast();
  const { location } = useLocation();
  const { mutate, isPending } = useUpdateBillLimit(location.locationId, reference);

  const cancelRequest = () => {
    if (isPending || !billLimit) {
      return;
    }

    mutate(
      {
        data: { maxAmount: billLimit.maxAmount ?? null, verifyWithSms: false },
        locationId: location.id,
        reference,
      },
      {
        onError: () => {
          toasts.pushError({
            id: `${reference}-cancel-limit-request`,
            message: <span>{intl.formatMessage({ id: 'digitalBill.billLimit.cancelRequest.error' })}</span>,
          });
        },
      }
    );
  };

  return { cancelRequest, isCancelRequestPending: isPending };
};
